import { datadogRum } from '@datadog/browser-rum';
import { ArrowBack, KeyboardArrowRight } from '@mui/icons-material';
import { Box, Button, IconButton, Stack } from '@mui/material';
import { formatName } from '@silbo/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useAuthenticationContext } from '../contexts/AuthenticationContext';
import { useHealthCenterContext } from '../contexts/HealthCenterContext';
import PasswordDial from '../features/patient-transport/components/PasswordDial';
import { getPortersQueryOptions } from '../features/patient-transport/queries';

export const Route = createFileRoute('/porter-login')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (!context.userAppContext.app) {
      throw redirect({ to: '/app-select' });
    }
    if (!context.healthCenterContext.healthCenterConfig?.id) {
      throw redirect({ to: '/health-center-link' });
    }

    if (context.authenticationContext.isAuthenticated) {
      throw redirect({ to: '/patient-transport/fares' });
    }
  },
  loader: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(
        getPortersQueryOptions(
          context.healthCenterContext.healthCenterConfig.id,
          context.healthCenterContext.healthCenterConfig.key
        )
      );
    } catch {
      context.healthCenterContext.resetHealthCenterConfig();
      throw redirect({ to: '/health-center-link' });
    }
  },
});

function RouteComponent() {
  const {
    healthCenterConfig: { id, key },
  } = useHealthCenterContext();
  const { resetLoginError } = useAuthenticationContext();
  const { resetHealthCenterConfig } = useHealthCenterContext();

  const [username, setUsername] = useState('');

  const { data: porters, isError } = useQuery(getPortersQueryOptions(id, key));

  const handleGoBack = () => {
    setUsername('');
    resetLoginError();
  };

  const handleSelectPorter = (username: string) => {
    datadogRum.addAction('select-porter');
    setUsername(username);
    resetLoginError();
  };

  useEffect(() => {
    if (isError) {
      resetHealthCenterConfig();
    }
  }, [isError, resetHealthCenterConfig]);

  return (
    <>
      {username ? (
        <>
          <Box sx={{ position: 'absolute', left: 16, top: 16 }}>
            <IconButton variant="filled" color="primary" onClick={handleGoBack}>
              <ArrowBack />
            </IconButton>
          </Box>
          <PasswordDial username={username} />
        </>
      ) : (
        <>
          <Stack spacing={4} padding="40px 16px">
            {porters?.map((porter) => (
              <Button
                key={porter.id}
                onClick={() => handleSelectPorter(porter.username)}
                endIcon={<KeyboardArrowRight />}
                data-dd-privacy="hidden"
                data-dd-action-name="hidden"
              >
                {formatName(porter.firstName, porter.lastName)}
              </Button>
            ))}
          </Stack>
        </>
      )}
    </>
  );
}
